//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import angular from 'angular';
import _ from 'underscore';

import template from './streams-drawer-action-bar.html';
import './streams-drawer-action-bar.less';

import { CollectionSortType } from '@apis/collections/CollectionSortType';
import { CollectionStatusType } from '@apis/collections/CollectionStatusType';
import { IShareCreatorClient } from '@share/models/shareStateModel';
import { IConnectVendorOption } from '@streams-drawer/model/IConnectVendorOption';
import { ISortOption } from '@streams-drawer/model/ISortOption';
import { IStream } from '@streams-drawer/model/IStream';
import { StreamsDrawerContentViews } from '@streams-drawer/streams-drawer-constants';
import { IStatusOption } from '../model/IStatussOption';

import TrackedSourceId from '@models/TrackedSourceId';
class StreamsDrawerActionBar {
  public StreamsDrawerContentViews;

  public isFiltersSidebarOpen: boolean;
  public isContentDiscoveryEnabled: boolean;
  public selectedStream: IStream;
  public creatorClients: IShareCreatorClient[];
  public onOpenFiltersSidebar: () => any;
  public onCloseFiltersSidebar: () => any;
  public sortFilterSetName: string;
  public sortFilterSetState: {
    orderBy: CollectionSortType[];
  };
  public isModerationServices: boolean;
  public moderationStatusFilterSetName: string;
  public moderationStatusFilterSetState: {
    moderationStatus: CollectionStatusType[];
  };
  public connectVendorFilterSetName: string;
  public connectVendorFilterSetState: {
    connectVendor: string[];
  };
  public sortFilterLockedKeys: string[];
  public sort: CollectionSortType;
  public moderationStatus: CollectionStatusType;
  public connectVendor: string;
  public sortOptions: ISortOption[];
  public sortOptionsBVNetwork: ISortOption[];
  public statusOptions: IStatusOption[];
  public connectVendorOptions: IConnectVendorOption[];
  public mentionsMyBrandFilterSetName: string;
  public mentionsMyBrandFilterSetState: {
    mentionsMyBrand: string[];
  };
  public mentionsMyBrand: string;
  public brandUserOptions: Array<{ value: string; label: string }> = [];

  public constructor(
    private COLLECTION_SETTINGS,
    private SEARCH_FILTERS,
    private SessionVarsService,
  ) {
    this.StreamsDrawerContentViews = StreamsDrawerContentViews;
  }

  public $onInit() {
    const sortedCreatorClients = this.creatorClients.sort((a, b) => {
      return a.name > b.name ? 1 : -1;
    });
    const streamSourceShareCreatorClientValues = sortedCreatorClients.map((c) =>
      c.id.toString(),
    );
    const streamSourceShareCreatorClientLabels = Object.assign(
      {},
      ...sortedCreatorClients.map((c) => {
        return {
          [c.id.toString()]: c.name,
        };
      }),
    );

    this.updateBrandUserOptions();

    this.sortFilterSetName = this.SEARCH_FILTERS.STREAMS_FILTERS_SORT;
    this.sortFilterSetState = { orderBy: [this.sort] };
    this.sortFilterLockedKeys = [];

    this.moderationStatusFilterSetName =
      this.SEARCH_FILTERS.STREAM_FILTERS_MODERATION_STATUS;
    this.moderationStatusFilterSetState = {
      moderationStatus: [this.moderationStatus],
    };

    this.connectVendorFilterSetName =
      this.SEARCH_FILTERS.STREAM_FILTERS_CONNECT_VENDOR;
    this.connectVendorFilterSetState = { connectVendor: [this.connectVendor] };

    this.mentionsMyBrandFilterSetName =
      this.SEARCH_FILTERS.STREAM_FILTERS_MENTIONS_STATUS;
    this.mentionsMyBrandFilterSetState = { mentionsMyBrand: [] };

    const {
      TIME,
      COMMENTS,
      LIKES,
      ENGAGEMENT,
      VISUAL_SCORE,
      SYNDICATION_TIMESTAMP,
      SIMILARITY_TO_APPROVED,
    } = this.COLLECTION_SETTINGS.SORT_KEYS;
    const { UNMODERATED, REJECTED, APPROVED } =
      this.COLLECTION_SETTINGS.STATUS_KEYS;
    const { ALL } = this.COLLECTION_SETTINGS.CONNECT_VENDOR_KEYS;

    // Change the order of this array to change when items show up first in the dropdown
    const sortValues = [
      TIME,
      COMMENTS,
      LIKES,
      ENGAGEMENT,
      VISUAL_SCORE,
      SYNDICATION_TIMESTAMP,
      SIMILARITY_TO_APPROVED,
    ];
    const statusValues = [UNMODERATED, REJECTED, APPROVED];
    const connectVendorValues = [ALL, ...streamSourceShareCreatorClientValues];

    const sortLabels = {
      [TIME]: 'Most Recent',
      [COMMENTS]: 'Most Comments',
      [LIKES]: 'Most Likes',
      [ENGAGEMENT]: 'Most Engaging',
      [VISUAL_SCORE]: 'Most On-Brand',
      [SYNDICATION_TIMESTAMP]: 'Recently Moderated',
      [SIMILARITY_TO_APPROVED]: 'Similarity to Approved',
    };
    const statusLabels = {
      [UNMODERATED]: 'Pending',
      [REJECTED]: 'Rejected',
      [APPROVED]: 'Published',
    };
    const connectVendorLabels = {
      [ALL]: 'All Brands',
      ...streamSourceShareCreatorClientLabels,
    };

    this.sortOptions = sortValues.map((sortValue) => {
      return {
        value: sortValue,
        label: sortLabels[sortValue],
      };
    });
    this.statusOptions = statusValues.map((statusValue) => {
      return {
        value: statusValue,
        label: statusLabels[statusValue],
      };
    });
    this.connectVendorOptions = connectVendorValues.map(
      (connectVendorValue) => {
        return {
          value: connectVendorValue,
          label: connectVendorLabels[connectVendorValue],
        };
      },
    );

    const isOnBrandEnabled =
      this.SessionVarsService.isFeatureEnabled('most_on_brand');

    if (!this.isContentDiscoveryEnabled && !isOnBrandEnabled) {
      this.sortOptions = _(this.sortOptions).reject((option) => {
        return option.value === this.COLLECTION_SETTINGS.SORT_KEYS.VISUAL_SCORE;
      });
    }

    const isSimilarityToApprovedEnabled =
      this.SessionVarsService.isFeatureEnabled('similarity_to_approved');

    if (!isSimilarityToApprovedEnabled) {
      this.sortOptions = _(this.sortOptions).reject((option) => {
        return (
          option.value ===
          this.COLLECTION_SETTINGS.SORT_KEYS.SIMILARITY_TO_APPROVED
        );
      });
    }
    this.sortOptionsBVNetwork = this.sortOptions;

    this.sortOptions = _(this.sortOptions).reject((option) => {
      return (
        option.value ===
        this.COLLECTION_SETTINGS.SORT_KEYS.SYNDICATION_TIMESTAMP
      );
    });
  }

  public $onChanges(changesObj: angular.IOnChangesObject) {
    if (
      changesObj.selectedStream &&
      !changesObj.selectedStream.isFirstChange()
    ) {
      this.updateBrandUserOptions();
    }
  }

  private updateBrandUserOptions() {
    const mentionUsernames: string[] = [];

    if (this.selectedStream?.sources?.instagramMentions) {
      this.selectedStream.sources.instagramMentions.forEach((mentionId) => {
        const userId = TrackedSourceId(mentionId);
        const instagramMentionsData =
          this.selectedStream?.sourcesMetadata?.instagramMentions || {};
        if (instagramMentionsData[userId]?.username) {
          mentionUsernames.push(instagramMentionsData[userId].username);
        }
      });
    }

    this.brandUserOptions = [
      { value: '', label: 'All Content' },
      ...mentionUsernames.map((username) => ({
        value: username,
        label: `Mentions @${username}`,
      })),
    ];
  }

  public toggleFiltersSidebar() {
    if (this.isFiltersSidebarOpen) {
      this.onCloseFiltersSidebar();
    } else {
      this.onOpenFiltersSidebar();
    }
  }
}

angular.module('curalate').component('streamsDrawerActionBar', {
  controller: [
    'COLLECTION_SETTINGS',
    'SEARCH_FILTERS',
    'SessionVarsService',
    StreamsDrawerActionBar,
  ],
  bindings: {
    selectedStream: '<',
    creatorClients: '<',
    contentView: '<',
    filterCount: '<',
    brandInstagramUsername: '<',
    isContentDiscoveryEnabled: '<',
    selectedStreamType: '<',
    isFiltersSidebarOpen: '<',
    isModerationServices: '<',
    onCloseFiltersSidebar: '&',
    onOpenFiltersSidebar: '&',
    onSetContentView: '&',
    sort: '<',
  },
  template,
});
